import { useState, useEffect, useRef, useId } from 'react';
import { SlideContent } from '../../services/mockSlideDeckService';
import { ChevronDown, ChevronRight, Lightbulb, AlertTriangle, Star } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';

// Initialize mermaid with larger, bolder text
mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  securityLevel: 'loose',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 16,
  flowchart: {
    nodeSpacing: 50,
    rankSpacing: 50,
    padding: 15,
    useMaxWidth: true,
  },
  themeVariables: {
    fontSize: '16px',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
});

interface SlideRendererProps {
  slide: SlideContent;
  compact?: boolean;
}

// Mermaid diagram renderer
const MermaidDiagram = ({ code, compact }: { code: string; compact?: boolean }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId().replace(/:/g, '-');
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const renderDiagram = async () => {
      try {
        // List of unsupported diagram types that might be generated by LLM
        const unsupportedTypes = ['vennDiagram', 'venn', 'mindmap', 'timeline', 'sankey', 'xy'];
        const firstLine = code.trim().split('\n')[0].toLowerCase();

        // Check if diagram type is unsupported
        if (unsupportedTypes.some(type => firstLine.includes(type.toLowerCase()))) {
          // Show fallback for unsupported diagram types
          setError(`Unsupported diagram type. Displaying as text.`);
          return;
        }

        // Sanitize mermaid code to fix common LLM mistakes
        let sanitizedCode = code
          // Remove any markdown code block markers
          .replace(/^```mermaid\s*/i, '')
          .replace(/```\s*$/i, '')
          // CRITICAL: Remove "note for" statements - NOT supported in flowchart
          .replace(/^\s*note\s+(for|right of|left of|over)\s+.*/gim, '')
          // Remove click statements - can cause parse errors
          .replace(/^\s*click\s+.*/gim, '')
          // Remove subgraph (keep diagrams flat to avoid complexity)
          .replace(/^\s*subgraph\s+.*/gim, '')
          .replace(/^\s*end\s*$/gim, '')
          // Fix subgraph titles with special characters (wrap in quotes) - in case we allow them later
          .replace(/subgraph\s+([^"\n\[]+)$/gm, (match, title) => {
            if (title.includes(' ') && !title.startsWith('"')) {
              return `subgraph "${title.trim()}"`;
            }
            return match;
          })
          // Convert LR (left-right) to TD (top-down) for better vertical fit
          .replace(/graph\s+LR/gi, 'graph TD')
          // Fix direction statements that are on the same line as graph
          .replace(/graph\s+(TD|LR|TB|RL);?\s*subgraph/gi, 'graph $1\n    subgraph')
          // Fix missing semicolons/newlines between statements
          .replace(/\]\s+subgraph/g, ']\n    subgraph')
          // Fix direction inside subgraph (remove if causes issues)
          .replace(/subgraph\s+direction\s+/gi, 'subgraph ')
          // Clean up any resulting empty lines
          .replace(/\n\s*\n\s*\n/g, '\n\n')
          // Fix node labels with parentheses - wrap in quotes for ALL node shapes
          // Square brackets: A[Text (with parens)]
          .replace(/(\w+)\[([^\]]*\([^\]]*\)[^\]]*)\]/g, (match, id, label) => {
            return `${id}["${label.replace(/"/g, "'")}"]`;
          })
          // Diamond/decision: A{Text (with parens)}
          .replace(/(\w+)\{([^}]*\([^}]*\)[^}]*)\}/g, (match, id, label) => {
            return `${id}{"${label.replace(/"/g, "'")}"}`;
          })
          // Stadium/pill shape: A(Text (with parens))
          .replace(/(\w+)\(([^)]*\([^)]*\)[^)]*)\)/g, (match, id, label) => {
            return `${id}("${label.replace(/"/g, "'")}")]`;
          })
          // Fix edge labels with parentheses
          .replace(/-->\s*\|([^|]*\([^|]*\)[^|]*)\|/g, (match, label) => {
            return `--> |"${label.replace(/"/g, "'")}"|`;
          })
          // Ensure proper line breaks
          .trim();

        const { svg } = await mermaid.render(`mermaid-${uniqueId}`, sanitizedCode);
        setSvg(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError('Failed to render diagram');
      }
    };

    renderDiagram();
  }, [code, uniqueId]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`
        flex items-center justify-center
        bg-gradient-to-br from-slate-50 via-white to-slate-50 
        rounded-2xl border border-slate-100
        ${compact ? 'p-2' : 'p-4'}
        w-full h-full
      `}
    >
      {/* SVG scales to fill container */}
      <div
        className="w-full h-full flex items-center justify-center [&_svg]:w-full [&_svg]:h-auto [&_svg]:max-h-full"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  );
};

// Reveal card component
const RevealCard = ({ question, answer }: { question: string; answer: string }) => {
  const [isRevealed, setIsRevealed] = useState(false);

  return (
    <button
      onClick={() => setIsRevealed(!isRevealed)}
      className="w-full text-left p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start gap-3">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isRevealed ? 'bg-amber-100' : 'bg-slate-100'
          }`}>
          {isRevealed ? (
            <ChevronDown className="w-4 h-4 text-amber-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700">{question}</p>
          {isRevealed && (
            <p className="text-sm text-slate-600 mt-3 pt-3 border-t border-slate-100">
              {answer}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

// Callout component
const Callout = ({ type, text, compact }: { type: 'tip' | 'warning' | 'key'; text: string; compact?: boolean }) => {
  const config = {
    tip: { icon: Lightbulb, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', iconColor: 'text-blue-500', label: 'Tip' },
    warning: { icon: AlertTriangle, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', iconColor: 'text-amber-500', label: 'Note' },
    key: { icon: Star, bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', iconColor: 'text-purple-500', label: 'Key Point' },
  };

  const { icon: Icon, bg, border, text: textColor, iconColor, label } = config[type];

  return (
    <div className={`flex items-start gap-3 ${compact ? 'p-2' : 'p-4'} rounded-xl ${bg} border ${border}`}>
      <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold ${iconColor} uppercase tracking-wide mb-1`}>{label}</p>
        <p className={`${compact ? 'text-xs' : 'text-sm'} ${textColor}`}>{text}</p>
      </div>
    </div>
  );
};

// Image component with loading state and optional attribution
interface SlideImageProps {
  url: string;
  alt: string;
  className?: string;
  // Attribution fields (TASL)
  author?: string;
  license?: string;
  sourceUrl?: string;
  showAttribution?: boolean;
}

const SlideImage = ({ url, alt, className, author, license, sourceUrl, showAttribution = true }: SlideImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Handle empty URL as "pending" - show placeholder
  if (!url || url === '') {
    return (
      <div className={`bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center ${className}`}>
        <div className="text-center text-slate-400">
          <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-slate-300 animate-pulse" />
          <span className="text-xs">Loading image...</span>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={`bg-slate-100 rounded-xl flex items-center justify-center ${className}`}>
        <span className="text-slate-400 text-sm">Image unavailable</span>
      </div>
    );
  }

  const hasAttribution = showAttribution && (author || license) && sourceUrl;

  return (
    <figure className={`relative overflow-hidden rounded-xl ${className}`}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-slate-100 animate-pulse" />
      )}
      <img
        src={url}
        alt={alt}
        className={`w-full h-full object-contain bg-slate-50 transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
      />
      {/* Subtle attribution overlay */}
      {hasAttribution && isLoaded && (
        <figcaption className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/40 to-transparent">
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[9px] text-white/70 hover:text-white/90 transition-colors truncate block"
          >
            {author && <span>{author}</span>}
            {license && <span className="ml-1 opacity-70">• {license}</span>}
          </a>
        </figcaption>
      )}
    </figure>
  );
};

export const SlideRenderer = ({ slide, compact = false }: SlideRendererProps) => {
  const { layout, content } = slide;

  // Skeleton loading state - show while slide is generating
  if (slide.status === 'generating') {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 font-slide">
        <div className="text-center space-y-4">
          {/* Animated spinner */}
          <div className="relative w-12 h-12 mx-auto">
            <div className="absolute inset-0 rounded-full border-4 border-slate-200" />
            <div className="absolute inset-0 rounded-full border-4 border-t-amber-500 animate-spin" />
          </div>
          {/* Slide title */}
          <p className={`text-slate-600 font-medium ${compact ? 'text-sm' : 'text-lg'}`}>
            {slide.title}
          </p>
          <p className="text-slate-400 text-sm">Generating slide...</p>
        </div>
      </div>
    );
  }

  // Title slide - Hero style with Swiss design
  if (layout === 'title') {
    return (
      <div className={`h-full relative overflow-hidden bg-gradient-to-br from-amber-50/50 via-white to-slate-50 font-slide`}>
        {content.image && (
          <div className="absolute inset-0">
            <img
              src={content.image.url}
              alt={content.image.alt}
              className="w-full h-full object-cover opacity-60"
            />
            {/* Stronger gradient overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-white/40" />
          </div>
        )}
        <div className={`relative z-10 h-full flex flex-col items-center justify-center text-center ${compact ? 'p-4' : 'p-12'}`}>
          {/* Title - larger and constrained width */}
          <h1 className={`font-bold text-slate-900 leading-tight tracking-tight max-w-2xl ${compact ? 'text-xl' : 'text-5xl'}`}>
            {content.heading}
          </h1>
          {/* Subtitle with amber accent color */}
          {content.subheading && (
            <p className={`text-amber-600 font-medium mt-4 max-w-xl ${compact ? 'text-sm' : 'text-xl'}`}>
              {content.subheading}
            </p>
          )}
          {content.body && !compact && (
            <p className="text-slate-500 mt-4 max-w-lg text-base leading-relaxed">
              {content.body}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Text + Image (image on right) - 40/60 split favoring image
  if (layout === 'text-image') {
    return (
      <div className={`h-full flex font-slide ${compact ? 'gap-4 p-4' : 'gap-6 p-6'}`}>
        {/* Text content - 40% */}
        <div className={`${compact ? 'w-[45%]' : 'w-[40%]'} flex flex-col justify-center`}>
          {content.heading && (
            <h2 className={`
              font-semibold text-slate-800 tracking-tight leading-tight
              ${compact ? 'text-base mb-2' : 'text-xl mb-3'}
            `}>
              {content.heading}
            </h2>
          )}
          {content.body && (
            <div className={`
              text-slate-600 leading-relaxed
              prose prose-slate max-w-none
              ${compact ? 'text-xs prose-xs' : 'text-sm prose-sm'}
              prose-p:my-1.5 prose-li:my-0.5
            `}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content.body}
              </ReactMarkdown>
            </div>
          )}
          {content.callout && !compact && (
            <div className="mt-4">
              <Callout type={content.callout.type} text={content.callout.text} />
            </div>
          )}
        </div>
        {/* Image - 60% */}
        {content.image && (
          <div className={`${compact ? 'w-[55%]' : 'w-[60%]'} flex items-center justify-center`}>
            <SlideImage
              url={content.image.url}
              alt={content.image.alt}
              className="w-full h-full object-contain rounded-xl shadow-lg"
            />
          </div>
        )}
      </div>
    );
  }

  // Image + Text (image on left) - 60/40 split favoring image
  if (layout === 'image-text') {
    return (
      <div className={`h-full flex font-slide ${compact ? 'gap-4 p-4' : 'gap-6 p-6'}`}>
        {/* Image - 60% */}
        {content.image && (
          <div className={`${compact ? 'w-[55%]' : 'w-[60%]'} flex items-center justify-center`}>
            <SlideImage
              url={content.image.url}
              alt={content.image.alt}
              className="w-full h-full object-contain rounded-xl shadow-lg"
            />
          </div>
        )}
        {/* Text content - 40% */}
        <div className={`${compact ? 'w-[45%]' : 'w-[40%]'} flex flex-col justify-center`}>
          {content.heading && (
            <h2 className={`
              font-semibold text-slate-800 tracking-tight leading-tight
              ${compact ? 'text-base mb-2' : 'text-xl mb-3'}
            `}>
              {content.heading}
            </h2>
          )}
          {content.body && (
            <div className={`
              text-slate-600 leading-relaxed
              prose prose-slate max-w-none
              ${compact ? 'text-xs prose-xs' : 'text-sm prose-sm'}
              prose-p:my-1.5 prose-li:my-0.5
            `}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content.body}
              </ReactMarkdown>
            </div>
          )}
          {content.callout && !compact && (
            <div className="mt-4">
              <Callout type={content.callout.type} text={content.callout.text} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Diagram slide - Centered diagram with context
  if (layout === 'diagram') {
    return (
      <div className={`h-full flex flex-col font-slide ${compact ? 'p-4' : 'p-6'}`}>
        {/* Header section - fixed height */}
        <div className="flex-shrink-0">
          {content.heading && (
            <h2 className={`
              font-semibold text-slate-800 tracking-tight
              ${compact ? 'text-base mb-1' : 'text-xl mb-2'}
            `}>
              {content.heading}
            </h2>
          )}
          {content.body && (
            <div className={`
              text-slate-600 leading-relaxed prose prose-slate
              ${compact ? 'text-xs mb-2 prose-sm' : 'text-base mb-3'}
            `}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.body}</ReactMarkdown>
            </div>
          )}
        </div>
        {/* Diagram - takes remaining space with better containment */}
        {content.diagram && (
          <div className={`
            flex-1 min-h-0 flex items-center justify-center 
            overflow-hidden
            ${compact ? 'max-h-[75%]' : 'max-h-[80%]'}
          `}>
            <MermaidDiagram code={content.diagram.code} compact={compact} />
          </div>
        )}
        {/* Footer section - fixed height */}
        <div className="flex-shrink-0">
          {content.bullets && !compact && (
            <div className="mt-3 flex flex-wrap gap-2">
              {content.bullets.map((bullet, i) => (
                <div key={i} className="px-3 py-1.5 bg-slate-100 rounded-full text-sm text-slate-600 font-medium">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children}</span> }}>
                    {bullet}
                  </ReactMarkdown>
                </div>
              ))}
            </div>
          )}
          {content.callout && !compact && (
            <div className="mt-3">
              <Callout type={content.callout.type} text={content.callout.text} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Two column layout
  if (layout === 'two-column') {
    return (
      <div className={`h-full flex flex-col font-slide ${compact ? 'p-3' : 'p-6'}`}>
        {content.heading && (
          <h2 className={`font-bold text-slate-800 ${compact ? 'text-base mb-3' : 'text-2xl mb-6'}`}>
            {content.heading}
          </h2>
        )}
        <div className={`flex-1 grid grid-cols-2 ${compact ? 'gap-3' : 'gap-8'}`}>
          <div className="bg-slate-50 rounded-xl p-4">
            {content.body && (
              <div className={`text-slate-600 prose prose-slate ${compact ? 'text-xs prose-xs' : 'text-sm prose-sm'} max-w-none`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content.body}
                </ReactMarkdown>
              </div>
            )}
          </div>
          <div className="bg-amber-50 rounded-xl p-4">
            {content.bullets && (
              <ul className={`space-y-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                {content.bullets.map((bullet, i) => (
                  <li key={i} className="text-slate-700 flex items-start gap-2">
                    <span className="text-amber-500 mt-1 flex-shrink-0">●</span>
                    <span className="flex-1">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children}</span> }}>
                        {bullet}
                      </ReactMarkdown>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {content.callout && !compact && (
          <div className="mt-4">
            <Callout type={content.callout.type} text={content.callout.text} />
          </div>
        )}
      </div>
    );
  }

  // Full image layout - Hero image with overlay text
  if (layout === 'full-image') {
    return (
      <div className="h-full relative overflow-hidden font-slide">
        {content.image && (
          <>
            <img
              src={content.image.url}
              alt={content.image.alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          </>
        )}
        <div className={`absolute bottom-0 left-0 right-0 text-white ${compact ? 'p-4' : 'p-10'}`}>
          {content.heading && (
            <h2 className={`font-bold ${compact ? 'text-lg' : 'text-3xl'} mb-2`}>
              {content.heading}
            </h2>
          )}
          {content.body && (
            <p className={`text-white/90 ${compact ? 'text-xs' : 'text-base'} max-w-2xl`}>
              {content.body}
            </p>
          )}
          {content.image?.caption && (
            <p className="text-white/60 text-xs mt-4 italic">{content.image.caption}</p>
          )}
        </div>
      </div>
    );
  }

  // Image grid - Multiple images in a grid
  if (layout === 'image-grid') {
    const images = content.images || [];
    return (
      <div className={`h-full flex flex-col font-slide ${compact ? 'p-3' : 'p-6'}`}>
        {content.heading && (
          <h2 className={`font-bold text-slate-800 ${compact ? 'text-base mb-3' : 'text-2xl mb-4'}`}>
            {content.heading}
          </h2>
        )}
        {content.body && (
          <p className={`text-slate-600 ${compact ? 'text-xs mb-3' : 'text-sm mb-6'}`}>
            {content.body}
          </p>
        )}
        <div className={`flex-1 grid ${images.length <= 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-4 min-h-0`}>
          {images.map((img, i) => (
            <div key={i} className="relative overflow-hidden rounded-xl group">
              <SlideImage
                url={img.url}
                alt={img.alt}
                className="w-full h-full"
              />
              {img.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-white text-xs">{img.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        {content.callout && !compact && (
          <div className="mt-4">
            <Callout type={content.callout.type} text={content.callout.text} />
          </div>
        )}
      </div>
    );
  }

  // Comparison layout - Side by side comparison with better space usage
  if (layout === 'comparison') {
    const comparison = content.comparison;
    return (
      <div className={`h-full flex flex-col font-slide ${compact ? 'p-3' : 'p-6'}`}>
        {content.heading && (
          <h2 className={`font-bold text-slate-800 text-center ${compact ? 'text-base mb-4' : 'text-2xl mb-6'}`}>
            {content.heading}
          </h2>
        )}
        {comparison && (
          <div className={`flex-1 grid grid-cols-2 ${compact ? 'gap-3' : 'gap-6'}`}>
            {/* Left side */}
            <div className={`rounded-2xl ${compact ? 'p-4' : 'p-6'} flex flex-col ${comparison.left.color || 'bg-blue-50 border-2 border-blue-200'}`}>
              <h3 className={`font-bold text-center mb-4 ${compact ? 'text-base' : 'text-xl'} ${comparison.left.color ? 'text-slate-800' : 'text-blue-800'}`}>
                {comparison.left.title}
              </h3>
              <ul className={`space-y-3 flex-1 ${compact ? 'text-sm' : 'text-base'}`}>
                {comparison.left.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className={`mt-1.5 flex-shrink-0 ${comparison.left.color ? 'text-slate-500' : 'text-blue-500'}`}>•</span>
                    <span className="text-slate-700 prose prose-slate prose-sm">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children}</span> }}>
                        {point}
                      </ReactMarkdown>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Right side */}
            <div className={`rounded-2xl ${compact ? 'p-4' : 'p-6'} flex flex-col ${comparison.right.color || 'bg-amber-50 border-2 border-amber-200'}`}>
              <h3 className={`font-bold text-center mb-4 ${compact ? 'text-base' : 'text-xl'} ${comparison.right.color ? 'text-slate-800' : 'text-amber-800'}`}>
                {comparison.right.title}
              </h3>
              <ul className={`space-y-3 flex-1 ${compact ? 'text-sm' : 'text-base'}`}>
                {comparison.right.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className={`mt-1.5 flex-shrink-0 ${comparison.right.color ? 'text-slate-500' : 'text-amber-500'}`}>•</span>
                    <span className="text-slate-700 prose prose-slate prose-sm">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children}</span> }}>
                        {point}
                      </ReactMarkdown>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {content.callout && !compact && (
          <div className="mt-6">
            <Callout type={content.callout.type} text={content.callout.text} />
          </div>
        )}
      </div>
    );
  }

  // Text only (default) - Clean list/summary style with improved typography
  return (
    <div className={`h-full flex flex-col font-slide ${compact ? 'p-4' : 'p-6'}`}>
      {content.heading && (
        <h2 className={`
          font-semibold text-slate-800 tracking-tight leading-tight
          ${compact ? 'text-base mb-3' : 'text-2xl mb-6'}
        `}>
          {content.heading}
        </h2>
      )}
      {content.body && (
        <div className={`
          text-slate-600 leading-relaxed
          prose prose-slate max-w-none
          ${compact ? 'text-xs mb-3 prose-xs' : 'text-base mb-6 prose-base'}
          prose-p:my-2 prose-li:my-1
        `}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content.body}
          </ReactMarkdown>
        </div>
      )}
      {content.bullets && (
        <ul className={`space-y-2 ${compact ? 'text-xs' : 'text-sm'}`}>
          {content.bullets.map((bullet, i) => (
            <li key={i} className={`
              flex items-start gap-3 
              ${compact ? 'p-2' : 'p-3'} 
              bg-gradient-to-r from-slate-50 to-transparent 
              rounded-xl border border-slate-100
            `}>
              <span className={`
                ${compact ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs'}
                rounded-full bg-amber-100 text-amber-600 
                flex items-center justify-center flex-shrink-0 font-bold
              `}>
                {i + 1}
              </span>
              <span className="flex-1 text-slate-700 leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children}</span> }}>
                  {bullet}
                </ReactMarkdown>
              </span>
            </li>
          ))}
        </ul>
      )}
      {content.revealCards && !compact && (
        <div className="mt-6 space-y-3">
          {content.revealCards.map((card, i) => (
            <RevealCard key={i} question={card.question} answer={card.answer} />
          ))}
        </div>
      )}
      {content.callout && !compact && (
        <div className="mt-6">
          <Callout type={content.callout.type} text={content.callout.text} />
        </div>
      )}
    </div>
  );
};
